export interface TreeNodeData {
  id: string
  label: string
  pathLabel?: string
  children?: TreeNodeData[]
}

export type TreeCheckState = 'checked' | 'unchecked' | 'indeterminate'

export function treeChildren(node: TreeNodeData): TreeNodeData[] {
  return node.children ?? []
}

export function collectTreeIds(nodes: TreeNodeData[]): Set<string> {
  const ids = new Set<string>()
  function walk(list: TreeNodeData[]) {
    for (const node of list) {
      ids.add(node.id)
      walk(treeChildren(node))
    }
  }
  walk(nodes)
  return ids
}

export function findTreeNode(
  nodes: TreeNodeData[],
  id: string,
): TreeNodeData | null {
  for (const node of nodes) {
    if (node.id === id) return node
    const child = findTreeNode(treeChildren(node), id)
    if (child) return child
  }
  return null
}

export function findTreePath(
  nodes: TreeNodeData[],
  id: string,
  ancestors: TreeNodeData[] = [],
): TreeNodeData[] | null {
  for (const node of nodes) {
    const path = [...ancestors, node]
    if (node.id === id) return path
    const nested = findTreePath(treeChildren(node), id, path)
    if (nested) return nested
  }
  return null
}

export function treePathLabel(
  node: TreeNodeData,
  ancestors: TreeNodeData[],
): string {
  if (node.pathLabel) return node.pathLabel
  return [...ancestors, node].map((item) => item.label).join(' · ')
}

export function collectExpandableIds(nodes: TreeNodeData[]): string[] {
  const ids: string[] = []
  for (const node of nodes) {
    if (treeChildren(node).length > 0) {
      ids.push(node.id)
      ids.push(...collectExpandableIds(treeChildren(node)))
    }
  }
  return ids
}

export function normalizeTreeSelection(
  selected: string[],
  tree: TreeNodeData[],
): string[] {
  const keys = new Set(selected)
  function dropCovered(nodes: TreeNodeData[], ancestorSelected: boolean) {
    for (const node of nodes) {
      if (ancestorSelected) keys.delete(node.id)
      dropCovered(treeChildren(node), ancestorSelected || keys.has(node.id))
    }
  }
  dropCovered(tree, false)
  const seen = new Set<string>()
  return selected.filter((id) => {
    if (!keys.has(id) || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

function subtreeHasSelection(keys: Set<string>, node: TreeNodeData): boolean {
  for (const child of treeChildren(node)) {
    if (keys.has(child.id) || subtreeHasSelection(keys, child)) return true
  }
  return false
}

export function treeCheckState(
  selected: string[],
  node: TreeNodeData,
  ancestorSelected = false,
): TreeCheckState {
  const keys = new Set(selected)
  if (ancestorSelected || keys.has(node.id)) return 'checked'
  if (subtreeHasSelection(keys, node)) return 'indeterminate'
  return 'unchecked'
}

function removeDescendants(keys: Set<string>, node: TreeNodeData) {
  for (const child of treeChildren(node)) {
    keys.delete(child.id)
    removeDescendants(keys, child)
  }
}

function expandAncestorsToSiblings(
  keys: Set<string>,
  tree: TreeNodeData[],
  targetId: string,
) {
  const path = findTreePath(tree, targetId) ?? []
  for (let index = 0; index < path.length - 1; index += 1) {
    const ancestor = path[index]!
    if (!keys.has(ancestor.id)) continue
    keys.delete(ancestor.id)
    for (const child of treeChildren(ancestor)) keys.add(child.id)
  }
}

export function toggleTreeSelection(
  selected: string[],
  tree: TreeNodeData[],
  targetId: string,
): string[] {
  const keys = new Set(normalizeTreeSelection(selected, tree))
  const node = findTreeNode(tree, targetId)
  if (!node) return [...keys]

  const path = findTreePath(tree, targetId) ?? []
  const ancestorSelected = path
    .slice(0, -1)
    .some((ancestor) => keys.has(ancestor.id))
  if (ancestorSelected) {
    expandAncestorsToSiblings(keys, tree, targetId)
    keys.delete(targetId)
    removeDescendants(keys, node)
    return normalizeTreeSelection([...keys], tree)
  }

  const children = treeChildren(node)
  if (children.length > 0) {
    if (keys.has(targetId)) {
      keys.delete(targetId)
      removeDescendants(keys, node)
      return normalizeTreeSelection([...keys], tree)
    }
    if (children.every((child) => keys.has(child.id))) {
      for (const child of children) keys.delete(child.id)
      keys.add(targetId)
      removeDescendants(keys, node)
      return normalizeTreeSelection([...keys], tree)
    }
    keys.delete(targetId)
    removeDescendants(keys, node)
    for (const child of children) keys.add(child.id)
    return normalizeTreeSelection([...keys], tree)
  }

  if (keys.has(targetId)) keys.delete(targetId)
  else keys.add(targetId)
  return normalizeTreeSelection([...keys], tree)
}

export function selectTreeSingle(targetId: string): string[] {
  return [targetId]
}

export function filterTree(
  nodes: TreeNodeData[],
  query: string,
): TreeNodeData[] {
  const keyword = query.trim().toLowerCase()
  if (!keyword) return nodes
  function match(node: TreeNodeData): TreeNodeData | null {
    const children = treeChildren(node).flatMap((child) => {
      const next = match(child)
      return next ? [next] : []
    })
    const self =
      node.label.toLowerCase().includes(keyword) ||
      (node.pathLabel?.toLowerCase().includes(keyword) ?? false)
    if (self || children.length > 0) return { ...node, children }
    return null
  }
  return nodes.flatMap((node) => {
    const next = match(node)
    return next ? [next] : []
  })
}

export function mergeOrphanTreeNodes(
  nodes: TreeNodeData[],
  value: string[],
): TreeNodeData[] {
  const known = collectTreeIds(nodes)
  const orphans = value
    .filter((id) => !known.has(id))
    .map((id) => ({ id, label: id, children: [] as TreeNodeData[] }))
  return orphans.length > 0 ? [...orphans, ...nodes] : nodes
}

export function collectTreeLabels(nodes: TreeNodeData[]): Map<string, string> {
  const labels = new Map<string, string>()
  function walk(list: TreeNodeData[]) {
    for (const node of list) {
      labels.set(node.id, node.pathLabel ?? node.label)
      walk(treeChildren(node))
    }
  }
  walk(nodes)
  return labels
}
